import Button from '@jetbrains/ring-ui/components/button/button'
import Checkbox from '@jetbrains/ring-ui/components/checkbox/checkbox'
import classNames from 'classnames'
import plur from 'plur'
import {memo, useCallback, useContext, useEffect, useMemo, useRef} from 'react'
import {
  Curve,
  VictoryAxis,
  VictoryChart,
  VictoryClipContainer,
  VictoryContainer,
  VictoryScatter,
} from 'victory'
import type {CurveProps} from 'victory-line'

import EntityPath from '../../../../../containers/EntityPath'
import {PatchedVictoryLine} from '../../../../../containers/PatchedVictory'
import {
  useBooleanQueryParam,
  useQueryParam,
  useQueryParamState,
  useSetQueryParams,
  useSyncedBooleanQueryParam,
} from '../../../../../hooks/routes'
import {useSetUserProperty} from '../../../../../hooks/useSetUserProperty'
import {useUserProperty} from '../../../../../hooks/useUserProperty'
import {getBuildType, getSnapshotDependencies} from '../../../../../selectors'
import type {BuildId, BuildTypeId, NormalizedBuild, ProjectId} from '../../../../../types'
import {stringifyId, toBuildId, toBuildTypeId} from '../../../../../types'
import {emptyArray, emptySet, getEmptyHash} from '../../../../../utils/empty'
import type {Point} from '../../../../../utils/geometry'
import type {KeyValue} from '../../../../../utils/object'
import {objectEntries} from '../../../../../utils/object'
import {UserProperties} from '../../../../../utils/userProperties'
import Loader from '../../../../common/Loader/Loader'
import RouterLink from '../../../../common/RouterLink/RouterLink'
import type {GraphNode, Layout} from '../../../PipelinesPages/utils/graph'
import {createGraph, createLayout} from '../../../PipelinesPages/utils/graph'

import {
  CHILD_HEIGHT,
  CHILD_WITH_DETAILS_HEIGHT,
  getPadding,
  HORIZONTAL_POINT_SPACING,
  ITEM_HEIGHT,
  ITEM_WITH_DETAILS_HEIGHT,
  LINE_OFFSET,
  POINT_SIZE,
  PROJECT_HEIGHT,
  SPACING,
} from './DependenciesChain.consts'
import type {Grouping} from './DependenciesChain.contexts'
import {GroupingContext, HighlightContext, IsDependantContext} from './DependenciesChain.contexts'
import {useChain} from './DependenciesChain.hooks'
import type {GraphData, NodeLabel} from './DependenciesChain.types'
import {isLeaf, isOnlyLeaf, isReused} from './DependenciesChain.utils'
import DependenciesChainItem from './DependenciesChainItem'
import legend from './Legend/Legend'

import styles from './DependenciesChain.css'

import {useAppSelector} from 'src/hooks/react-redux'

function Nothing() {
  return null
}

const emptyFragment = <></>

const getRange = (values: number[]): [number, number] => [Math.min(...values), Math.max(...values)]

function getDomain({nodes, edges}: GraphData, itemHeight: number): Domain {
  const nodePoints: ReadonlyArray<Point> = nodes.flatMap(node => [
    node,
    {...node, y: node.y - (node.height ?? itemHeight)},
  ])
  const points = nodePoints.concat(...edges.map(edge => edge.points))
  const x = getRange(points.map(point => point.x))
  const y = getRange(points.map(node => node.y))
  const padding = getPadding(itemHeight)
  return {
    x,
    y,
    width: x[1] - x[0] + padding.left + padding.right,
    height: y[1] - y[0] + padding.top + padding.bottom,
  }
}

const OFFSET_X = 0.3
const SIMPLE_EDGE_LENGTH = 3

const mix = (a: number, b: number, ratio: number) => a * (1 - ratio) + b * ratio

function transformPoints(points: Point[]) {
  const start = points[0]
  const end = points[points.length - 1]
  // const isSpecial = points.some(({y}) => y > Math.max(start.y, end.y) || y < Math.min(start.y, end.y))
  const isSpecial = points.length > SIMPLE_EDGE_LENGTH

  if (isSpecial) {
    return points
  }

  return [
    start,
    {
      x: mix(start.x, end.x, OFFSET_X),
      y: start.y,
    },
    {
      x: mix(end.x, start.x, OFFSET_X),
      y: end.y,
    },
    end,
  ]
}

type HighlightedCurveProps = CurveProps & {
  from?: BuildTypeId
  to: BuildTypeId
}
function HighlightedCurve({from, to, ...curveProps}: HighlightedCurveProps) {
  const highlighted = useContext(HighlightContext)
  const classes = classNames(styles.highlightedEdge, {
    [styles.hidden]: from == null || !highlighted.has(from) || !highlighted.has(to),
  })
  return (
    <Curve
      {...curveProps}
      className={classes}
      style={{
        stroke: undefined,
      }}
    />
  )
}

type Domain = {
  x: [number, number]
  y: [number, number]
  width: number
  height: number
}
type ChartProps = {
  domain?: Domain
  graphData: GraphData
  projectId?: ProjectId
}
const Chart = memo(function Chart({domain, graphData, projectId}: ChartProps) {
  const showDetails = useBooleanQueryParam('showDetails')
  const padding = getPadding(showDetails ? ITEM_WITH_DETAILS_HEIGHT : ITEM_HEIGHT)

  return (
    <>
      <VictoryChart
        domain={domain}
        width={domain?.width}
        height={domain?.height}
        containerComponent={
          <VictoryContainer
            title="Dependencies chain"
            responsive={false}
            className={styles.chartContainer}
          />
        }
        padding={padding}
      >
        <VictoryAxis groupComponent={<Nothing />} />
        {graphData.edges.map(({key, points}) => (
          <PatchedVictoryLine
            groupComponent={
              <VictoryClipContainer
                clipPadding={{
                  top: 1,
                  bottom: 1,
                }}
              />
            }
            dataComponent={
              <Curve
                className={styles.edge}
                style={{
                  stroke: undefined,
                }}
              />
            }
            key={key}
            data={points}
            interpolation="basis"
          />
        ))}
        {graphData.edges.map(({from, to, key, points}) => (
          <PatchedVictoryLine
            groupComponent={
              <VictoryClipContainer
                clipPadding={{
                  top: 1,
                  bottom: 1,
                }}
              />
            }
            dataComponent={<HighlightedCurve from={from} to={to} />}
            key={key}
            data={points}
            interpolation="basis"
          />
        ))}
      </VictoryChart>
      <VictoryChart
        domain={domain}
        width={domain?.width}
        height={domain?.height}
        containerComponent={emptyFragment}
        padding={padding}
      >
        <VictoryAxis groupComponent={<Nothing />} />
        <VictoryScatter
          data={graphData.nodes}
          size={POINT_SIZE / 2}
          groupComponent={emptyFragment}
          dataComponent={
            <DependenciesChainItem graph={graphData.graph} chainProjectId={projectId} />
          }
        />
      </VictoryChart>
    </>
  )
})
const UNRELATED_PREFIX = 'unrelated '

const getGraphData = (
  graph: Record<string, GraphNode>,
  layout: Layout<NodeLabel>,
  itemHeight: number,
): GraphData => ({
  nodes: Array.from(layout.dag?.nodes() ?? []).map(node => {
    const {x = 0, y = 0} = node
    const {data} = node.data
    return {
      ...data,
      x: y,
      y: data.height != null ? x + data.height / 2 - itemHeight / 2 : x,
    }
  }),
  edges:
    Array.from(layout.dag?.links() ?? []).map(link => {
      const points: Point[] = link.points.map(([x, y]) => ({x: y, y: x}))
      const from = link.source.data.data
      const to = link.target.data.data
      if (from.isProject) {
        points[0].y = link.source.x ?? 0
      }
      if (to.isProject) {
        points[points.length - 1].x -= POINT_SIZE / 2 + LINE_OFFSET
      }
      return {
        key: `${from.id} => ${to.id}`,
        from: from.id,
        to: to.id,
        points: transformPoints(points),
      }
    }) ?? [],
  graph,
})

const prefixProjectId = (projectId: ProjectId) => `project:${projectId}`

type Props = {
  withLegend?: boolean
}

function DependenciesChain({withLegend = true}: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const selectedBuildId = useQueryParam('selectedBuildId')
  const {self, ready, allBuilds, buildsByBuildType, buildTypesHash, buildTypes} = useChain()
  const reusedCount = allBuilds.filter(build => isReused(build, self)).length
  const highlightReused = useBooleanQueryParam('highlightReused')
  const snapshotDependencies: KeyValue<BuildId, ReadonlyArray<BuildId>> = useAppSelector(state =>
    ready ? getSnapshotDependencies(state) : getEmptyHash(),
  )
  const projectId = useAppSelector(state => getBuildType(state, self?.buildType)?.projectId)
  const allBuildsHash: KeyValue<BuildId, NormalizedBuild> = useMemo(
    () => Object.fromEntries(allBuilds.map(build => [build.id, build])),
    [allBuilds],
  )
  const [showDetails, setShowDetails] = useSyncedBooleanQueryParam({
    param: 'showDetails',
    userProperty: UserProperties.SHOW_CHAIN_DETAILS,
  })
  const itemHeight = showDetails ? ITEM_WITH_DETAILS_HEIGHT : ITEM_HEIGHT

  const unfilteredGraph = useMemo(() => {
    if (!ready) {
      return null
    }

    const nodes = []

    for (const [buildTypeId, buildTypeBuilds = []] of objectEntries(buildsByBuildType)) {
      const node: NodeLabel = {
        id: buildTypeId,
        projectId: buildTypesHash[buildTypeId]?.projectId,
        isComposite: buildTypesHash[buildTypeId]?.type === 'composite',
        dependencies: [],
      }
      nodes.push(node)
      for (const build of buildTypeBuilds) {
        const dependencies: ReadonlyArray<BuildId> = snapshotDependencies[build.id] ?? emptyArray

        const getBuildTypeId = (dependency: BuildId) => allBuildsHash[dependency]?.buildType

        node.isComposite ||= build.composite

        for (const dependency of dependencies) {
          const dependencyBuildTypeId = getBuildTypeId(dependency)

          if (dependencyBuildTypeId != null && !node.dependencies.includes(dependencyBuildTypeId)) {
            node.dependencies.push(dependencyBuildTypeId)
          }
        }

        const unrelatedCount = dependencies.filter(
          dependency => getBuildTypeId(dependency) == null,
        ).length

        if (unrelatedCount > 0) {
          const unrelatedId = UNRELATED_PREFIX + stringifyId(buildTypeId)
          const unrelatedNode = nodes.find(item => item.id === unrelatedId)

          if (unrelatedNode == null) {
            nodes.push({id: unrelatedId, unrelatedCount, dependencies: []})
            node.dependencies.push(unrelatedId)
          } else if ((unrelatedNode.unrelatedCount ?? 0) < unrelatedCount) {
            unrelatedNode.unrelatedCount = unrelatedCount
          }
        }
      }
    }

    for (const buildType of buildTypes) {
      if (buildsByBuildType[buildType.id!] == null) {
        const dependencies = buildType['snapshot-dependencies']?.['snapshot-dependency'] ?? []

        const isRelated = (dependency: {id?: BuildTypeId}) =>
          dependency.id === self?.buildType || buildTypes.some(item => item.id === dependency.id)

        const nodeDependencies: string[] = []
        nodes.push({
          id: stringifyId(buildType.id),
          projectId: buildType.projectId,
          isComposite: buildType.type === 'composite',
          dependencies: nodeDependencies,
        })

        for (const dependency of dependencies) {
          if (isRelated(dependency)) {
            nodeDependencies.push(dependency.id!)
          }
        }

        const unrelatedCount = dependencies.filter(dependency => !isRelated(dependency)).length

        if (unrelatedCount > 0) {
          const unrelatedId = UNRELATED_PREFIX + stringifyId(buildType.id)
          nodes.push({id: unrelatedId, unrelatedCount, dependencies: []})
          nodeDependencies.push(unrelatedId)
        }
      }
    }

    return createGraph({
      data: nodes,
      getId: node => node.id,
      getDependencies: node => node.dependencies,
    })
    // We want to calculate graph only once after data load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready])

  const [groupAllComposite, setGroupAllComposite] = useQueryParamState('groupAllComposite')
  const [groupAllProjects, setGroupAllProjects] = useQueryParamState('groupAllProjects')
  const groupComposite = useQueryParam('groupComposite')
  const ungroupComposite = useQueryParam('ungroupComposite')
  const [ungroupProjects, setUngroupProjects] = useQueryParamState('ungroupProjects')
  const setQueryParams = useSetQueryParams()
  const compositeBuildTypeIds = useMemo(
    () =>
      unfilteredGraph &&
      Object.keys(unfilteredGraph).filter(
        id => unfilteredGraph[id]?.data.isComposite && !isOnlyLeaf(unfilteredGraph, id),
      ),
    [unfilteredGraph],
  )
  const compositeCount = compositeBuildTypeIds?.length ?? 0
  const joinedUngroupCompositeChainIds =
    useUserProperty(UserProperties.UNGROUP_COMPOSITE_BUILDS_IN_CHAIN) ?? ''
  const joinedGroupProjectsChainIds = useUserProperty(UserProperties.GROUP_PROJECTS_IN_CHAIN) ?? ''
  const ungroupCompositeChainIds = useMemo(
    () => joinedUngroupCompositeChainIds.split(','),
    [joinedUngroupCompositeChainIds],
  )
  const buildTypeInternalId = useAppSelector(
    state => getBuildType(state, self?.buildType)?.internalId,
  )
  const isGroupAllComposite =
    groupAllComposite != null
      ? groupAllComposite !== 'false'
      : buildTypeInternalId == null || !ungroupCompositeChainIds.includes(buildTypeInternalId)
  const groupProjectsChainIds = useMemo(
    () => joinedGroupProjectsChainIds.split(','),
    [joinedGroupProjectsChainIds],
  )
  const isGroupAllProjects =
    groupAllProjects != null
      ? groupAllProjects === 'true'
      : buildTypeInternalId != null && groupProjectsChainIds.includes(buildTypeInternalId)
  useEffect(() => {
    const isGroupAllCompositeString = String(isGroupAllComposite)
    if (isGroupAllCompositeString !== groupAllComposite) {
      setGroupAllComposite(isGroupAllCompositeString)
    }
  }, [groupAllComposite, isGroupAllComposite, setGroupAllComposite])
  useEffect(() => {
    const isGroupAllProjectsString = String(isGroupAllProjects)
    if (isGroupAllProjectsString !== groupAllProjects) {
      setGroupAllProjects(isGroupAllProjectsString)
    }
  }, [groupAllProjects, isGroupAllProjects, setGroupAllProjects])
  const groupCompositeIds = useMemo(
    () => groupComposite?.split(',').map(toBuildTypeId),
    [groupComposite],
  )
  const ungroupCompositeIds = useMemo(
    () => ungroupComposite?.split(',').map(toBuildTypeId),
    [ungroupComposite],
  )
  const ungroupProjectIds = useMemo(() => ungroupProjects?.split(','), [ungroupProjects])
  const grouping: Grouping = useMemo(
    () => ({
      isGroupedComposite: id =>
        isGroupAllComposite ||
        groupCompositeIds?.includes(id) ||
        (ungroupCompositeIds != null && !ungroupCompositeIds.includes(id)),
      isGroupedProject: id =>
        isGroupAllProjects && (ungroupProjectIds == null || !ungroupProjectIds.includes(id)),
      getUngrouped: () => ({
        buildTypes:
          ungroupCompositeIds ||
          (groupCompositeIds != null &&
            compositeBuildTypeIds?.filter(id => !groupCompositeIds.includes(id))) ||
          [],
        projects: (isGroupAllProjects && ungroupProjectIds) || [],
      }),
      groupComposite(id, grouped) {
        if (grouped) {
          if (ungroupCompositeIds != null) {
            const updated = ungroupCompositeIds.filter(otherId => otherId !== id)

            if (updated.length === 0) {
              setQueryParams({
                groupAllComposite: 'true',
                ungroupComposite: null,
              })
            } else {
              setQueryParams({
                ungroupComposite: updated.map(stringifyId).join(','),
              })
            }
          } else {
            const updated = groupCompositeIds?.concat(id) ?? [id]

            if (updated.length === compositeCount) {
              setQueryParams({
                groupAllComposite: 'true',
                groupComposite: null,
              })
            } else {
              setQueryParams({
                groupComposite: updated.map(stringifyId).join(','),
              })
            }
          }
        } else if (isGroupAllComposite) {
          setQueryParams({
            groupAllComposite: 'false',
            ungroupComposite: stringifyId(id),
          })
        } else if (ungroupCompositeIds != null) {
          const updated = ungroupCompositeIds.concat(id)
          setQueryParams({
            ungroupComposite:
              updated.length < compositeCount ? updated.map(stringifyId).join(',') : null,
          })
        } else if (groupCompositeIds != null) {
          const updated = groupCompositeIds.filter(otherId => otherId !== id)
          setQueryParams({
            groupComposite: updated.length > 0 ? updated.map(stringifyId).join(',') : null,
          })
        }
      },
      groupProject(id, grouped) {
        if (grouped) {
          const updated = ungroupProjectIds?.filter(otherId => otherId !== id) ?? []
          setUngroupProjects(updated.length === 0 ? null : updated.join(','))
        } else {
          const updated = (ungroupProjectIds ?? []).concat(id)
          if (updated.length < compositeCount) {
            setUngroupProjects(updated.join(','))
          } else {
            setQueryParams({
              groupAllProjects: 'false',
              ungroupProjects: null,
            })
          }
        }
      },
    }),
    [
      compositeBuildTypeIds,
      compositeCount,
      groupCompositeIds,
      isGroupAllComposite,
      isGroupAllProjects,
      setQueryParams,
      setUngroupProjects,
      ungroupCompositeIds,
      ungroupProjectIds,
    ],
  )
  const graph = useMemo(() => {
    if (unfilteredGraph == null) {
      return unfilteredGraph
    }

    const nodesHash: Record<string, NodeLabel> = {}
    const includedNodes = new Set()

    const nodes = Object.values(unfilteredGraph)
    nodes
      .filter(
        node =>
          isLeaf(unfilteredGraph, node.id) || node.id === self?.buildType || node.data.isComposite,
      )
      .forEach(node => {
        const process = (id: BuildTypeId | undefined) => {
          if (id == null || includedNodes.has(id)) {
            return
          }

          includedNodes.add(id)

          if (
            !grouping.isGroupedComposite(id) ||
            !unfilteredGraph[id]?.data.isComposite ||
            isOnlyLeaf(unfilteredGraph, id)
          ) {
            unfilteredGraph[id]?.optimizedDependencies.forEach(process)
          }
        }

        process(node.id)
      })
    nodes
      .filter(node => isLeaf(unfilteredGraph, node.id))
      .forEach(node => {
        const process = (id: BuildTypeId | undefined, dependantId?: BuildTypeId) => {
          if (id != null && includedNodes.has(id)) {
            nodesHash[id] ??= {...unfilteredGraph[id]?.data, dependencies: []}

            if (dependantId != null && !nodesHash[dependantId].dependencies.includes(id)) {
              nodesHash[dependantId].dependencies.push(id)
            }

            unfilteredGraph[id]?.optimizedDependencies.forEach(dependencyId =>
              process(dependencyId, id),
            )
          } else if (id != null) {
            unfilteredGraph[id]?.optimizedDependencies.forEach(dependencyId =>
              process(dependencyId, dependantId),
            )
          }
        }

        process(node.id)
      })

    let result = createGraph({
      data: Object.values(nodesHash),
      getId: node => node.id,
      getDependencies: node => node.dependencies,
    })
    if (isGroupAllProjects) {
      const groups: Record<ProjectId, Set<BuildTypeId>> = {}
      for (const id of Object.keys(nodesHash)) {
        const node = nodesHash[id]
        if (
          node.projectId != null &&
          grouping.isGroupedProject(node.projectId) &&
          id !== self?.buildType &&
          id != null &&
          (!grouping.isGroupedComposite(id) || !node.isComposite || isOnlyLeaf(result, id))
        ) {
          groups[node.projectId] ??= new Set()
          groups[node.projectId].add(id)
        }
      }
      Object.entries(groups).forEach(([groupProjectId, ids]) => {
        const visited = new Set()
        const process = (from: string, to: string) => {
          if (!ids.has(from) && ids.has(to)) {
            ids.delete(to) // this item introduces cycle and should be excluded from group
            visited.delete(to)
          }
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          processOutEdges(to)
        }
        const processOutEdges = (from: string) => {
          if (!visited.has(from)) {
            visited.add(from)
            result[from]?.dependants.forEach(to => process(from, to))
          }
        }

        for (const id of ids) {
          if (ids.size > 1) {
            processOutEdges(id)
          }
        }
        if (ids.size > 1) {
          const groupId = prefixProjectId(groupProjectId)
          const dependencies: string[] = []
          nodesHash[groupId] = {
            id: groupId,
            projectId: groupProjectId,
            isProject: true,
            height:
              PROJECT_HEIGHT + ids.size * (showDetails ? CHILD_WITH_DETAILS_HEIGHT : CHILD_HEIGHT),
            children: [...ids].map(id => unfilteredGraph[id]?.data),
            dependencies,
          }
          for (const id of ids) {
            result[id]?.dependencies.forEach(dependency => {
              if (!ids.has(dependency) && !dependencies.includes(dependency)) {
                dependencies.push(dependency)
              }
            })
            result[id]?.dependants.forEach(dependant => {
              if (
                !ids.has(dependant) &&
                nodesHash[dependant] != null &&
                !nodesHash[dependant].dependencies.includes(groupId)
              ) {
                nodesHash[dependant].dependencies = [
                  ...nodesHash[dependant].dependencies.filter(dependency => !ids.has(dependency)),
                  groupId,
                ]
              }
            })
            delete nodesHash[id]
          }
        }
      })

      result = createGraph({
        data: Object.values(nodesHash),
        getId: node => node.id,
        getDependencies: node => node.dependencies,
      })
    }

    return result
  }, [unfilteredGraph, isGroupAllProjects, self?.buildType, grouping, showDetails])

  const graphData = useMemo(() => {
    if (graph == null) {
      return null
    }

    const layout = createLayout(graph, node => [
      (node.data.height ?? itemHeight) + SPACING / 2,
      HORIZONTAL_POINT_SPACING,
    ])
    return getGraphData(graph, layout, itemHeight)
  }, [graph, itemHeight])

  const selectedBuildTypeId =
    selectedBuildId != null ? allBuildsHash[toBuildId(selectedBuildId)]?.buildType : null
  const selectedProjectId = useQueryParam('selectedProjectId')
  const selfBuildTypeId = self?.buildType
  const isDependant = useCallback(
    (buildTypeId: BuildTypeId) => graph?.[selfBuildTypeId!]?.successors.has(buildTypeId) ?? false,
    [graph, selfBuildTypeId],
  )
  const highlighted = useMemo((): Set<BuildTypeId> => {
    const selectedId =
      selectedBuildTypeId ?? (selectedProjectId != null ? prefixProjectId(selectedProjectId) : null)
    if (selectedId == null) {
      return emptySet
    }

    return new Set([
      selectedId,
      ...(graph?.[selectedId]?.predecessors ?? []),
      ...(graph?.[selectedId]?.successors ?? []),
    ])
  }, [graph, selectedBuildTypeId, selectedProjectId])
  const domain = useMemo(
    () => (graphData != null ? getDomain(graphData, itemHeight) : undefined),
    [graphData, itemHeight],
  )
  const readyToRender = graphData != null

  const setUserProperty = useSetUserProperty()
  const compositeGroupingCheckbox = (
    <Checkbox
      checked={isGroupAllComposite}
      onChange={e => {
        const {checked} = e.currentTarget
        setQueryParams({
          groupAllComposite: checked ? 'true' : 'false',
          groupComposite: null,
          ungroupComposite: null,
        })
        if (buildTypeInternalId != null) {
          const newUngroupCompositeChainIds = checked
            ? ungroupCompositeChainIds.filter(id => id !== buildTypeInternalId)
            : ungroupCompositeChainIds.concat(buildTypeInternalId)
          setUserProperty(
            UserProperties.UNGROUP_COMPOSITE_BUILDS_IN_CHAIN,
            newUngroupCompositeChainIds.join(','),
          )
        }
      }}
    >
      {'Group composite builds'}
    </Checkbox>
  )
  const ungrouped = grouping.getUngrouped()

  return (
    <div className={styles.container}>
      <div
        className={classNames(styles.panelContainer, {
          [styles.hidden]: !readyToRender,
        })}
      >
        <div className={styles.panel}>
          <div className={styles.settings}>
            {reusedCount > 0 && (
              <span className={styles.reusedNote}>
                {'Reused '}
                <RouterLink
                  params={{
                    highlightReused: highlightReused ? null : 'true',
                  }}
                  withMergeParams
                >
                  {reusedCount} {plur('build', reusedCount)}
                </RouterLink>
                {'.'}
              </span>
            )}
            <Checkbox checked={showDetails} onChange={e => setShowDetails(e.target.checked)}>
              {'Show details'}
            </Checkbox>
            {compositeCount > 0 && compositeGroupingCheckbox}
            <Checkbox
              checked={isGroupAllProjects}
              onChange={e => {
                const {checked} = e.currentTarget
                setGroupAllProjects(checked ? 'true' : 'false')
                if (buildTypeInternalId != null) {
                  const newFroupProjectsChainIds = checked
                    ? groupProjectsChainIds.concat(buildTypeInternalId)
                    : groupProjectsChainIds.filter(id => id !== buildTypeInternalId)
                  setUserProperty(
                    UserProperties.GROUP_PROJECTS_IN_CHAIN,
                    newFroupProjectsChainIds.join(','),
                  )
                }
              }}
            >
              {'Group by project'}
            </Checkbox>
          </div>
          {(ungrouped.buildTypes.length > 0 || ungrouped.projects.length > 0) && (
            <div className={styles.ungrouped}>
              <span className={styles.ungroupedLabel}>{'Ungrouped:'}</span>
              {ungrouped.buildTypes.map(id => (
                <div key={id} className={styles.ungroupedItem}>
                  <EntityPath
                    withLeafIcon
                    withLeafStatusIcon={false}
                    className={styles.ungroupedPath}
                    projectOrBuildTypeNode={null}
                    buildTypeId={id}
                  />
                  <Button
                    className={styles.groupButton}
                    text
                    onClick={() => grouping.groupComposite(id, true)}
                  >
                    {'group'}
                  </Button>
                </div>
              ))}
              {ungrouped.projects.map(id => (
                <div key={id} className={styles.ungroupedItem}>
                  <EntityPath
                    withLeafIcon
                    withLeafStatusIcon={false}
                    className={styles.ungroupedPath}
                    projectOrBuildTypeNode={null}
                    projectId={id}
                  />
                  <Button
                    className={styles.groupButton}
                    text
                    onClick={() => grouping.groupProject(id, true)}
                  >
                    {'group'}
                  </Button>
                </div>
              ))}
            </div>
          )}
          {withLegend && legend}
        </div>
      </div>
      <div
        ref={ref}
        className={classNames(styles.innerContainer, {
          [styles.highlightReused]: highlightReused,
        })}
      >
        {readyToRender && graphData != null && self != null ? (
          <GroupingContext.Provider value={grouping}>
            <HighlightContext.Provider value={highlighted}>
              <IsDependantContext.Provider value={isDependant}>
                <Chart domain={domain} graphData={graphData} projectId={projectId} />
              </IsDependantContext.Provider>
            </HighlightContext.Provider>
          </GroupingContext.Provider>
        ) : (
          <Loader className={styles.loader} />
        )}
      </div>
    </div>
  )
}

export default DependenciesChain
